import { useMemo, useState } from 'react'

import { CrosshairIcon } from 'lucide-react'
import { Button, DefaultTooltip } from 'twyne-new.ui'

import { TruncateWithTooltipFormatter } from '@Components/Grid/components/Table/formatters'

import { useCustomReportStore } from '../store'
import { getChartGroupingLabel } from '../utils/chartIntervals'
import { applyChartFilter, buildChartFromTable, collapsesChartIndex } from '../utils/chartUtils'
import { getMetricDisplayLabel } from '../utils/collectionMetrics'
import { getChartColSpanClass, getDefaultChartName } from '../utils/constants'

import ChartDisplayPopover from './ChartDisplayPopover'
import ChartFilterPopover, { EMPTY_CHART_FILTER } from './ChartFilterPopover'
import ChartResult from './ChartResult'
import ChartSettingsPopover from './ChartSettingsPopover'

function ChartPanel({ chart }) {
  const {
    config: { value: config },
    table: { data: tableData, status: tableStatus },
    charts: { data: chartsData },
  } = useCustomReportStore()

  const entry = chartsData[chart.id]
  const data = entry?.data ?? tableData
  const groupby = entry?.groupby ?? config.groupby

  const chartResult = useMemo(
    () => buildChartFromTable(data, chart, groupby, config.collection),
    [data, chart, groupby, config.collection],
  )

  const [compareSeries, setCompareSeries] = useState(false)
  const [chartFilter, setChartFilter] = useState(EMPTY_CHART_FILTER)

  const canCompareSeries = chart.dataType === 'line-chart'

  const filteredResult = useMemo(
    () => applyChartFilter(chartResult, chartFilter),
    [chartResult, chartFilter],
  )

  const displayResult = useMemo(() => {
    const grouping = getChartGroupingLabel(chart, config)
    if (!grouping) return filteredResult

    return { ...filteredResult, indexLabel: grouping.charAt(0).toUpperCase() + grouping.slice(1) }
  }, [chart, config, filteredResult])

  const unsupportedLabels = useMemo(
    () => (filteredResult.unsupportedKeys || []).map(key => getMetricDisplayLabel(config.collection, { key })),
    [filteredResult.unsupportedKeys, config.collection],
  )

  const missingAxis = !collapsesChartIndex(chart.dataType) && !chart.dimensions?.length

  const chartTitle = useMemo(() => {
    if (chart.name?.trim()) return chart.name.trim()
    return getDefaultChartName(config, chart.metricKeys)
  }, [chart.name, chart.metricKeys, config])

  return (
    <div className="rounded-sm border twy-border-base twy-bg-base-accent p-4 h-full flex flex-col">
      <div className="flex items-center justify-between gap-3 mb-3 shrink-0">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <TruncateWithTooltipFormatter
            value={chartTitle}
            className="text-lg font-semibold twy-text-base"
          />
          <ChartDisplayPopover chart={chart} />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {canCompareSeries && <DefaultTooltip
            content="Compare all series at the hovered point"
            triggerProps={{ asChild: true }}
          >
            <Button
              type="button"
              size="sm"
              className="px-1"
              variant={compareSeries ? 'light' : 'ghost'}
              color={compareSeries ? 'accent' : 'neutral'}
              aria-label="Compare all series"
              aria-pressed={compareSeries}
              onClick={() => setCompareSeries(prev => !prev)}
            >
              <CrosshairIcon size={16} />
            </Button>
          </DefaultTooltip>}
          <ChartFilterPopover
            result={chartResult}
            collection={config.collection}
            filter={chartFilter}
            onChange={setChartFilter}
          />
          <ChartSettingsPopover chart={chart} />
        </div>
      </div>

      {unsupportedLabels.length > 0 && <div className="mb-3 rounded-sm px-3 py-2 text-sm twy-text-danger twy-bg-base">
        {unsupportedLabels.join(', ')} cannot be summed across the group by levels left out of this chart. Add the missing levels or pick an additive metric.
      </div>}

      {missingAxis ? <div className="font-semibold py-8 text-center twy-text-base-muted">
        This chart has no x axis. Pick one on the chart title to plot it.
      </div> : tableStatus !== 'loaded' ? <div className="font-semibold py-8 text-center twy-text-base-muted">
        Run the report to generate chart data from the table.
      </div> : <ChartResult
        dataType={chart.dataType}
        result={displayResult}
        collection={config.collection}
        compareSeries={canCompareSeries && compareSeries}
      />
      }
    </div>
  )
}

export default function ChartsSection() {
  const { charts: { items } } = useCustomReportStore()

  if (!items.length) return null

  return (
    <div className="mb-4 grid grid-cols-12 gap-4 grid-flow-dense">
      {items.map(chart => <div key={chart.id} className={getChartColSpanClass(chart.layoutSize)}>
        <ChartPanel chart={chart} />
      </div>)}
    </div>
  )
}
